import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, LinkIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { shareAPI } from '../services/api';
import { toast } from 'sonner';

const ShareModal = ({ groupId, groupName, settlement, onClose }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateLink = async () => {
    setLoading(true);
    try {
      const response = await shareAPI.createLink({ group_id: groupId });
      const token = response.data.token;
      const url = `${window.location.origin}/share/${token}`;
      setShareUrl(url);
    } catch (error) {
      toast.error('Gagal membuat link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link berhasil disalin!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      toast.success('Link berhasil disalin!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareWhatsApp = () => {
    const message = settlement
      ? `Halo ${settlement.from}! Ini reminder dari Patungin:\n\nKamu masih punya hutang ke *${settlement.to}* sebesar *Rp ${settlement.amount.toLocaleString('id-ID')}*\nGrup: ${groupName}\n\n${shareUrl ? `Detail lengkap: ${shareUrl}` : ''}\n\nYuk segera di-settle!`
      : `Cek detail patungan grup *${groupName}* di Patungin:\n\n${shareUrl || 'Klik "Buat Link" untuk generate link'}`;

    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-[60] flex items-end sm:items-center justify-center" onClick={onClose}>
        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white rounded-t-[32px] sm:rounded-3xl p-6 w-full max-w-md shadow-[0_-10px_40px_rgba(0,0,0,0.1)]"
          onClick={(e) => e.stopPropagation()}>

          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-6 sm:hidden"></div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Bagikan</h3>
            <button onClick={onClose} data-testid="close-share-modal"
              className="w-8 h-8 rounded-full bg-surface flex items-center justify-center hover:bg-surface-hover transition-colors">
              <XMarkIcon className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {settlement && (
            <div className="bg-surface p-4 rounded-2xl mb-5">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{settlement.from}</span> bayar ke{' '}
                <span className="font-semibold text-slate-900">{settlement.to}</span>
              </p>
              <p className="text-xl font-bold text-danger mt-1">
                Rp {settlement.amount.toLocaleString('id-ID')}
              </p>
            </div>
          )}

          {/* Generate Link */}
          {!shareUrl ? (
            <button onClick={generateLink} disabled={loading} data-testid="generate-share-link"
              className="w-full flex items-center justify-center gap-3 p-4 bg-surface border border-border rounded-3xl hover:bg-surface-hover transition-colors mb-3 disabled:opacity-50">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <LinkIcon className="w-5 h-5 text-primary" />
              </div>
              <span className="font-semibold text-slate-900">{loading ? 'Membuat link...' : 'Buat Link Publik'}</span>
            </button>
          ) : (
            <div className="mb-3">
              <div className="flex items-center gap-2 p-3 bg-surface border border-border rounded-2xl">
                <input type="text" value={shareUrl} readOnly data-testid="share-url-input"
                  className="flex-1 text-sm text-slate-700 bg-transparent outline-none truncate" />
                <button onClick={copyLink} data-testid="copy-share-link"
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    copied ? 'bg-success text-white' : 'bg-primary text-white hover:bg-primary-hover'
                  }`}>
                  {copied ? <CheckIcon className="w-4 h-4" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {/* WhatsApp */}
          <button onClick={shareWhatsApp} data-testid="share-whatsapp-btn"
            className="w-full flex items-center justify-center gap-3 p-4 bg-[#25D366]/10 border border-[#25D366]/30 rounded-3xl hover:bg-[#25D366]/20 transition-colors">
            <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </div>
            <span className="font-semibold text-[#25D366]">Kirim via WhatsApp</span>
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareModal;
